import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/motion";
import { usePageMeta } from "@/lib/seo";
import { LeafMark } from "@/components/LeafMark";

export const MedicalDisclaimer = () => {
    const { t, tHtml } = useLang();
    usePageMeta(
        t("meta.medical_disclaimer.title", "Medical Disclaimer"),
        t("meta.medical_disclaimer.desc", "UniversHeal is a healthcare coordination service and does not itself diagnose or provide medical treatment. Read our full medical disclaimer.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("medical_disclaimer.eyebrow", "Legal")}
                title={t("medical_disclaimer.title", "Medical Disclaimer")}
                intro={t("medical_disclaimer.intro", "Please read this notice carefully — it explains what UniversHeal is, and what it is not.")}
            />
            <section className="section-pad pt-6">
                <div className="container-site max-w-3xl">
                    <Reveal>
                        <div
                            data-testid="disclaimer-statement"
                            className="bg-forest text-ivory rounded-2xl p-8 sm:p-12"
                        >
                            <LeafMark className="w-9 h-9 text-natural" />
                            <p className="mt-8 font-serif italic text-2xl sm:text-[1.8rem] leading-snug">
                                {t("medical_disclaimer.statement", "UniversHeal coordinates access to healthcare services and does not itself diagnose or provide medical treatment unless expressly stated.")}
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-14 space-y-10">
                        {[
                            {
                                h: t("medical_disclaimer.h1", "No doctor–patient relationship"),
                                p: t("medical_disclaimer.p1", "Using this website or engaging our coordination services does not create a doctor–patient relationship with UniversHeal. Medical care is provided only by independent, licensed hospitals and clinicians, under their own professional responsibility."),
                            },
                            {
                                h: t("medical_disclaimer.h2", "Information is general"),
                                p: t("medical_disclaimer.p2", "All content on this website — including pages about specialties, hospitals and the patient journey — is provided for general information only. It is not medical advice, diagnosis or a treatment recommendation, and must not be relied upon as such."),
                            },
                            {
                                h: t("medical_disclaimer.h3", "Outcomes cannot be promised"),
                                p: t("medical_disclaimer.p3", "No content on this website constitutes a promise or guarantee of any medical outcome. Treatment results depend on many factors and rest with the medical professionals who provide your care."),
                            },
                            {
                                h: t("medical_disclaimer.h4", "Wellness & Ayurveda"),
                                p: t("medical_disclaimer.p4", "UniversHeal coordinates healthcare, wellness and Ayurvedic experiences through external providers. Medical diagnosis and treatment decisions remain the responsibility of qualified healthcare professionals and providers. Wellness and Ayurvedic services may vary by provider and individual circumstances. Users should discuss medical conditions, medications and treatment decisions with qualified healthcare professionals."),
                            },
                            {
                                h: t("medical_disclaimer.h5", "In an emergency"),
                                p: t("medical_disclaimer.p5", "UniversHeal is not an emergency service. If you are experiencing a medical emergency, contact your local emergency number or seek immediate care at the nearest emergency facility."),
                            },
                        ].map((item, i) => (
                            <Reveal key={i}>
                                <div data-testid={`disclaimer-${i + 1}`}>
                                    <h2 className="font-serif font-medium text-2xl sm:text-3xl text-forest">
                                        {item.h}
                                    </h2>
                                    <p className="mt-4 text-[15px] leading-relaxed text-charcoal/80">
                                        {item.p}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};
